import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { SectionTitle, Card, Field, Area, SaveButton } from "../ui";

function ListEditor({ title, items, fields, onChange, addLabel, testid }) {
  const list = items || [];
  const setItem = (i, key, val) => {
    const next = [...list];
    next[i] = { ...next[i], [key]: val };
    onChange(next);
  };
  const add = () => onChange([...list, Object.fromEntries(fields.map((f) => [f.key, ""]))]);
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium text-charcoal-800">{title}</h3>
        <button data-testid={`${testid}-add`} onClick={add} className="inline-flex items-center gap-1 text-sm text-sage-700 hover:text-terracotta">
          <Plus size={15} /> {addLabel || "Add"}
        </button>
      </div>
      <div className="space-y-5">
        {list.map((item, i) => (
          <div key={i} className="rounded-lg border border-sand-200 p-4 space-y-3 relative">
            <button data-testid={`${testid}-del-${i}`} onClick={() => remove(i)} className="absolute top-3 right-3 text-charcoal-400 hover:text-destructive">
              <Trash2 size={16} />
            </button>
            {fields.map((f) =>
              f.area ? (
                <Area key={f.key} label={f.label} testid={`${testid}-${i}-${f.key}`} value={item[f.key]} onChange={(v) => setItem(i, f.key, v)} rows={2} />
              ) : (
                <Field key={f.key} label={f.label} testid={`${testid}-${i}-${f.key}`} value={item[f.key]} onChange={(v) => setItem(i, f.key, v)} />
              )
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}

function StringListEditor({ title, items, onChange, testid }) {
  const list = items || [];
  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium text-charcoal-800">{title}</h3>
        <button data-testid={`${testid}-add`} onClick={() => onChange([...list, ""])} className="inline-flex items-center gap-1 text-sm text-sage-700 hover:text-terracotta">
          <Plus size={15} /> Add
        </button>
      </div>
      <div className="space-y-2">
        {list.map((val, i) => (
          <div key={i} className="flex gap-2">
            <input
              data-testid={`${testid}-${i}`}
              className="w-full bg-white border border-sand-200 rounded-lg px-3 py-2 text-sm text-charcoal-800 focus:outline-none focus:ring-2 focus:ring-sage/50"
              value={val}
              onChange={(e) => { const n = [...list]; n[i] = e.target.value; onChange(n); }}
            />
            <button data-testid={`${testid}-del-${i}`} onClick={() => onChange(list.filter((_, idx) => idx !== i))} className="text-charcoal-400 hover:text-destructive shrink-0 px-2">
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function MeridaManager() {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/merida-profile").then((res) => setP(res.data));
  }, []);

  const save = async () => {
    setSaving(true);
    const { _id, ...payload } = p;
    await api.put("/admin/merida-profile", payload);
    toast.success("Mérida page saved");
    setSaving(false);
  };

  if (!p) return <p className="text-charcoal-500">Loading…</p>;

  const set = (key, val) => setP({ ...p, [key]: val });
  const setNested = (parent, key, val) => setP({ ...p, [parent]: { ...(p[parent] || {}), [key]: val } });

  return (
    <div data-testid="admin-merida">
      <SectionTitle title="Mérida page" subtitle="Edit the rich Mérida destination page — facts, neighborhoods, costs, and the welcome & community sections." />
      <div className="space-y-6">
        <Card>
          <h3 className="font-medium text-charcoal-800 mb-4">Hero & intro</h3>
          <div className="space-y-4">
            <Field label="Hero title" testid="merida-hero-title" value={p.hero_title} onChange={(v) => set("hero_title", v)} />
            <Area label="Hero subtitle" testid="merida-hero-subtitle" value={p.hero_subtitle} onChange={(v) => set("hero_subtitle", v)} rows={2} />
            <Field label="Hero image URL" testid="merida-hero-image" value={p.hero_image} onChange={(v) => set("hero_image", v)} />
            <Area label="Intro paragraph" testid="merida-intro" value={p.intro} onChange={(v) => set("intro", v)} />
          </div>
        </Card>

        <ListEditor title="Quick facts" testid="merida-stats" items={p.stats} addLabel="Add fact"
          fields={[{ key: "label", label: "Label" }, { key: "value", label: "Value" }]}
          onChange={(v) => set("stats", v)} />

        <ListEditor title="Why Mérida (cards)" testid="merida-why" items={p.why} addLabel="Add reason"
          fields={[{ key: "title", label: "Title" }, { key: "text", label: "Text", area: true }]}
          onChange={(v) => set("why", v)} />

        <Card>
          <h3 className="font-medium text-charcoal-800 mb-4">Welcome / inclusivity section</h3>
          <div className="space-y-4">
            <Field label="Section title" testid="merida-welcome-title" value={p.welcome?.title} onChange={(v) => setNested("welcome", "title", v)} />
            <Area label="Intro" testid="merida-welcome-intro" value={p.welcome?.intro} onChange={(v) => setNested("welcome", "intro", v)} />
          </div>
        </Card>
        <ListEditor title="Welcome — groups" testid="merida-welcome-groups" items={p.welcome?.groups} addLabel="Add group"
          fields={[{ key: "title", label: "Group title" }, { key: "text", label: "Text", area: true }]}
          onChange={(v) => setNested("welcome", "groups", v)} />

        <Card>
          <h3 className="font-medium text-charcoal-800 mb-4">Expat community section</h3>
          <div className="space-y-4">
            <Field label="Section title" testid="merida-expat-title" value={p.expat?.title} onChange={(v) => setNested("expat", "title", v)} />
            <Area label="Text" testid="merida-expat-text" value={p.expat?.text} onChange={(v) => setNested("expat", "text", v)} />
          </div>
        </Card>
        <StringListEditor title="Expat — ways to connect" testid="merida-expat-ways" items={p.expat?.ways} onChange={(v) => setNested("expat", "ways", v)} />

        <Card>
          <h3 className="font-medium text-charcoal-800 mb-1">Testimonials</h3>
          <p className="text-sm text-charcoal-500 mb-4">Add authentic quotes from real clients (e.g. Black and LGBTQ+ movers). Replace the seeded examples with genuine testimonials.</p>
          <div className="space-y-4">
            <Field label="Section title" testid="merida-testimonials-title" value={p.testimonials_title} onChange={(v) => set("testimonials_title", v)} />
            <Area label="Section intro" testid="merida-testimonials-intro" value={p.testimonials_intro} onChange={(v) => set("testimonials_intro", v)} rows={2} />
          </div>
        </Card>
        <ListEditor title="Testimonial quotes" testid="merida-testimonials" items={p.testimonials} addLabel="Add testimonial"
          fields={[{ key: "quote", label: "Quote", area: true }, { key: "name", label: "Name" }, { key: "detail", label: "Detail (e.g. 'LGBTQ+ couple · from Chicago, IL')" }]}
          onChange={(v) => set("testimonials", v)} />

        <ListEditor title="Neighborhoods" testid="merida-neighborhoods" items={p.neighborhoods} addLabel="Add neighborhood"
          fields={[{ key: "name", label: "Name" }, { key: "character", label: "Character (short)" }, { key: "blurb", label: "Blurb", area: true }]}
          onChange={(v) => set("neighborhoods", v)} />

        <ListEditor title="Cost snapshot" testid="merida-costs" items={p.costs} addLabel="Add cost row"
          fields={[{ key: "label", label: "Label" }, { key: "range", label: "Range" }]}
          onChange={(v) => set("costs", v)} />

        <Card>
          <h3 className="font-medium text-charcoal-800 mb-4">Living details</h3>
          <div className="space-y-4">
            <Area label="Climate" testid="merida-climate" value={p.climate} onChange={(v) => set("climate", v)} />
            <Area label="Healthcare" testid="merida-healthcare" value={p.healthcare} onChange={(v) => set("healthcare", v)} />
            <Area label="Community" testid="merida-community" value={p.community} onChange={(v) => set("community", v)} />
            <Area label="Compare note" testid="merida-compare" value={p.compare_note} onChange={(v) => set("compare_note", v)} />
          </div>
        </Card>

        <SaveButton testid="merida-save" onClick={save} saving={saving} />
      </div>
    </div>
  );
}
